import merge from 'lodash/merge';
import pick from 'lodash/pick';
import type { NodeEntry } from 'slate';
import { Editor, Node, Path, Transforms } from 'slate';

import { mergeWithDefaults } from '../../../util';
import type { AlineText, ParagraphElement, TabElement } from '../../internal';
import { isParagraphElement, isTabElement, Marks, Queries } from '../../internal';

export function normalizeText(editor: Editor, textEntry: NodeEntry<AlineText>): void {
  const [textNode, textPath] = textEntry;

  /**
   * A paragraph whose entire content was deleted (e.g. an editing-mode delete
   * over suggested-deleted text) can be left holding a single empty text node
   * that still carries suggestion marks. A suggestion on zero characters can
   * never be accepted or rejected — both resolve by text range, and a
   * zero-width range never matches — so the mark would linger as an invisible
   * pending edit. Strip it.
   *
   * Only the only-child case is a husk. Zero-width marks at the edges of a
   * paragraph that still has content are deliberately preserved by
   * `normalizeParagraph` (they record a suggestion continuing across the
   * paragraph boundary), and marked empties between text siblings are removed
   * wholesale by the same rule.
   */
  if (
    textNode.text === '' &&
    Array.isArray(textNode.suggestedEdits) &&
    textNode.suggestedEdits.length > 0
  ) {
    const [parentNode] = Editor.parent(editor, textPath);
    if (isParagraphElement(parentNode) && parentNode.children.length === 1) {
      Transforms.setNodes(editor, { suggestedEdits: [] }, { at: textPath, voids: true });
      return;
    }
  }

  const { text, ...textProps } = textNode;
  const defaultTextProps = getTextProps(editor, textEntry);
  const hasInvalidComments = !Array.isArray(textNode.comments);
  const hasInvalidSuggestedEdits = !Array.isArray(textNode.suggestedEdits);
  const missingKeys = Object.keys(defaultTextProps).filter(textPropKey => {
    const hasKey = textPropKey in textNode;
    return !hasKey;
  });
  if (!missingKeys.length && !hasInvalidComments && !hasInvalidSuggestedEdits) return;

  const backFilledProps: Omit<AlineText, 'text'> = mergeWithDefaults(
    {
      ...defaultTextProps,
      ...textProps,
      comments: textNode.comments ?? [],
      suggestedEdits: textNode.suggestedEdits ?? [],
    },
    defaultTextProps
  );
  Transforms.setNodes(editor, backFilledProps, { at: textPath, voids: true });
}

/**
 * Utility function for getting the props that should be assigned to a text node if any props are missing.
 * Defaults to the props/formats that would come from the style variant of the parent paragraph.
 * Optimistically uses the props/formats from the text before the text that is missing keys.
 */
function getTextProps(editor: Editor, textEntry: NodeEntry<AlineText>): Omit<AlineText, 'text'> {
  const [, textPath] = textEntry;
  const paragraphEntry = Editor.above<ParagraphElement>(editor, {
    at: textPath,
    match: isParagraphElement,
  });
  if (!paragraphEntry) return { ...Marks.default, comments: [], suggestedEdits: [] };

  const [paragraph] = paragraphEntry;
  const styleSheet = Queries.activeStyleSheet(editor);
  const styleVariant = Queries.renderedParagraphStyleVariant(paragraph);
  const paragraphFontStyles =
    styleVariant in styleSheet ? styleSheet[styleVariant].styles : Marks.default;
  const inheritedTextProps: Omit<AlineText, 'text'> = {
    ...paragraphFontStyles,
    comments: [],
    suggestedEdits: [],
  };

  // For text inside tab elements, try to preserve styles from adjacent text
  // Check if the text is inside a tab element
  const tabEntry = Editor.above<TabElement>(editor, {
    at: textPath,
    match: isTabElement,
  });

  // Use the formats from the nearest text before/after the text, if we can.
  const adjacentText = findAdjacentTextLeaf(editor, textPath);
  if (adjacentText) {
    // For text inside tab elements, preserve more properties including bold and underline
    if (tabEntry) {
      const adjacentTextProps = pick(adjacentText, [
        'fontSize',
        'fontType',
        'fontColor',
        'bold',
        'underline',
        'italic',
        'strikethrough',
      ]);
      return merge({}, inheritedTextProps, adjacentTextProps);
    }
    // Only keep props that are likely to be the same for most of the document
    const adjacentTextProps = pick(adjacentText, ['fontSize', 'fontType', 'fontColor']);
    return merge({}, inheritedTextProps, adjacentTextProps);
  }

  return inheritedTextProps;
}

/**
 * The nearest text leaf before `textPath` in document order, falling back to
 * the nearest one after it. Walks the tree structurally with `Node.texts`
 * instead of `Editor.before`/`Editor.after`: the point walkers compute an end
 * point for every block they pass, so they throw on a paragraph missing its
 * trailing text node — a shape normalization must be able to walk past in
 * order to heal it (see the trailing-inline rule in `normalizeParagraph`).
 */
function findAdjacentTextLeaf(editor: Editor, textPath: Path): AlineText | undefined {
  for (const [node, path] of Node.texts(editor, { from: textPath, reverse: true })) {
    if (!Path.equals(path, textPath)) return node;
  }
  for (const [node, path] of Node.texts(editor, { from: textPath })) {
    if (!Path.equals(path, textPath)) return node;
  }
  return undefined;
}
